'use client';

import { FC, ReactNode, useRef } from 'react';

import {
  Box,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';

import { glassyEffectModal } from '@/styles/variants/glassy';

import { FeedbackContent } from './FeedbackContent';
import { useCloseOnChatBoxScroll } from './useCloseOnChatBoxScroll';

export interface MessageFeedbackPopoverProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onSubmitFeedback: (data: {
    feedback: string;
    reasons: string[];
  }) => Promise<void>;
}

export const MessageFeedbackPopover: FC<MessageFeedbackPopoverProps> = ({
  children,
  isOpen,
  onClose,
  onSubmitFeedback,
}) => {
  const initialFocusRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useCloseOnChatBoxScroll(triggerRef, isOpen, onClose);

  return (
    <Popover
      offset={[0, 12]}
      isOpen={isOpen}
      onClose={onClose}
      placement='top'
      closeOnBlur
      closeOnEsc={false}
      strategy='fixed'
      initialFocusRef={initialFocusRef}
      eventListeners={{ scroll: true, resize: true }}
    >
      <PopoverTrigger>
        <Box as='span' ref={triggerRef} display='inline-block'>
          {children}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          autoFocus
          bg='transparent'
          border='none'
          boxShadow='none'
          p={0}
          _focus={{ boxShadow: 'none' }}
          _focusVisible={{ boxShadow: 'none' }}
        >
          <Box
            sx={glassyEffectModal}
            borderRadius='16px'
            left={{ base: 0, md: 'calc(50% - 120px)' }}
            mx={{ base: 4, md: 0 }}
            w={{ base: 'calc(100vw - 32px)', md: '395px' }}
            maxW={{ base: 'calc(100vw - 32px)', md: '395px' }}
            overflow='hidden'
          >
            <FeedbackContent
              initialFocusRef={initialFocusRef}
              onAccept={onSubmitFeedback}
              onCancel={onClose}
            />
          </Box>
          <PopoverArrow bgColor='white.100' />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
